import * as VideoConference from 'video-conference';
import AgoraRTC from "agora-rtc-sdk-ng";
import VideoManager from "./VideoManager";

export default class DevicesManager implements VideoConference.DevicesManagerInterface {
    public user: VideoConference.UserData = {
        cameraStatus: true,
        microphoneStatus: true,
        audioStatus: true,
    };
    private camerasList: Array<VideoConference.MediaDeviceInfo> = [];
    private microphonesList: Array<VideoConference.MediaDeviceInfo> = [];
    private micId: string|null = null;
    private cameraId: string|null = null;

    private videoManager: VideoManager | null = null;

    private readonly PREVIEW_VIDEO_CONTAINER = 'AgoraUserVideoPreview';
    private readonly CONFERENCE_VIDEO_CONTAINER = 'localUserVideo';

    public async initDevices(): Promise<void> {
        await AgoraRTC.getDevices()
            .then((devices: Array<MediaDeviceInfo>) => {
                this.microphonesList = [];
                this.camerasList = [];

                devices.forEach((device: MediaDeviceInfo) => {
                    if (device.kind === "audioinput") {
                        this.microphonesList.push({
                            id: device.deviceId,
                            name: device.label,
                            group: device.groupId,
                        });
                    }

                    if (device.kind === "videoinput") {
                        this.camerasList.push({
                            id: device.deviceId,
                            name: device.label,
                            group: device.groupId,
                        });
                    }
                });

                if (typeof this.camerasList[0] !== 'undefined' && !this.cameraId) {
                    this.cameraId = this.camerasList[0].id;
                }

                if (typeof this.microphonesList[0] !== 'undefined' && !this.micId) {
                    this.micId = this.microphonesList[0].id;
                }

                if (document.getElementById(this.CONFERENCE_VIDEO_CONTAINER)) {
                    this.createVideoTracks(this.CONFERENCE_VIDEO_CONTAINER);
                }
            });
    }

    public getCameras(): Array<VideoConference.MediaDeviceInfo>
    {
        return this.camerasList;
    }

    public getMicrophones(): Array<VideoConference.MediaDeviceInfo>
    {
        return this.microphonesList;
    }

    public async changeCamera(id: string): Promise<void>
    {
        this.cameraId = id;

        if (!this.videoManager) {
            return;
        }

        this.videoManager.localTracks.videoTrack?.stop();
        this.videoManager.localTracks.videoTrack?.close();
        this.videoManager.localTracks.videoTrack = await AgoraRTC.createCameraVideoTrack({
            cameraId: id,
        });

        this.videoManager.localTracks.videoTrack.play(this.PREVIEW_VIDEO_CONTAINER, {mirror: false});
    }

    public async changeMicrophone(id: string): Promise<void>
    {
        this.micId = id;

        if (!this.videoManager) {
            return;
        }

        this.videoManager.localTracks.audioTrack?.stop();
        this.videoManager.localTracks.audioTrack?.close();
        this.videoManager.localTracks.audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
            microphoneId: id,
            AEC: true,
            ANS: true,
        });
    }

    public getAudioStatus(): boolean
    {
        return this.user.audioStatus;
    }

    public getMicrophoneStatus(): boolean
    {
        return this.user.microphoneStatus;
    }

    public getCameraStatus(): boolean
    {
        return this.user.cameraStatus;
    }

    public getCameraId(): string | null
    {
        return this.cameraId;
    }

    public getMicId(): string | null
    {
        return this.micId;
    }

    public async setCallSettings(): Promise<void>
    {
        if (!this.videoManager) {
            return;
        }

        if (this.videoManager.localTracks.audioTrack && this.micId) {
            await this.videoManager.localTracks.audioTrack.setDevice(this.micId).then(() => {
                console.log('audio connected')
            });
        }

        if (this.videoManager.localTracks.videoTrack && this.cameraId) {
            await this.videoManager.localTracks.videoTrack.setDevice(this.cameraId).then(() => {
                console.log('video connected');
            });
        }

        this.videoManager.publishTracks().then(() => {
            if (!this.videoManager) {
                return;
            }

            this.videoManager.closeSettings();
        });
    }

    public setCameraStatus(status: boolean): void
    {
        this.user.cameraStatus = status;
    }

    public setMicrophoneStatus(status: boolean): void
    {
        this.user.microphoneStatus = status;
    }

    public setVideoManager(manager: VideoManager): void
    {
        this.videoManager = manager;
    }

    stopLocalMedia(): void
    {
    }

    public async getLocalMedia(element: HTMLElement): Promise<any>
    {
        if (!this.videoManager) {
            return;
        }

        await this.initDevices().then(() => {
            this.createVideoTracks(element.id);
        });
    }

    setAudioStatus(status: boolean): void {
    }

    private async createVideoTracks(containerId: string) {
        await Promise.all([
            AgoraRTC.createCameraVideoTrack({
                cameraId: this.cameraId ?? undefined,
            }),
            AgoraRTC.createMicrophoneAudioTrack({
                microphoneId: this.micId ?? undefined,
                AEC: true,
                ANS: true,
            }),
        ]).then(([videoTrack, audioTrack]) => {

            if (this.videoManager) {
                this.videoManager.localTracks.videoTrack = videoTrack;
                this.videoManager.localTracks.audioTrack = audioTrack;

                let holder: HTMLElement|null = document.getElementById(containerId);
                if (holder) {
                    holder.innerHTML = '';
                }

                this.videoManager.localTracks.videoTrack.play(containerId, {mirror: false});
            }

            // /*setInterval(() => {
            //     const level = audioTrack.getVolumeLevel();
            //     console.log("local stream audio level", level);
            // }, 1000);*/
        });
    }
}
